import { DerivativeKind } from "./galleryPaths";

export interface ResizeSpec {
  kind: DerivativeKind;
  maxEdge: number;
  quality: number;
}

export const THUMB: ResizeSpec = { kind: "thumb", maxEdge: 640, quality: 0.72 };
export const WEB: ResizeSpec = { kind: "web", maxEdge: 1600, quality: 0.8 };

export const fitDimensions = (
  width: number,
  height: number,
  maxEdge: number
): { width: number; height: number } => {
  if (width <= maxEdge && height <= maxEdge) return { width, height };
  const scale = maxEdge / Math.max(width, height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const toBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

/**
 * Decode, downscale and re-encode a photo entirely in the browser.
 * Returns null if the image cannot be decoded, in which case the caller
 * should upload the original alone rather than fail the whole upload.
 */
export const resizeImage = async (
  file: File,
  spec: ResizeSpec
): Promise<{ blob: Blob; contentType: string } | null> => {
  // "from-image" applies the EXIF orientation tag, so phone photos are not
  // stored sideways. TypeScript 4.9's DOM lib predates the value (it knows only
  // "flipY" | "none"), hence the cast; browsers that don't support it ignore
  // the option and the fallback below handles the ones that reject it.
  const orientedOptions = {
    imageOrientation: "from-image",
  } as unknown as ImageBitmapOptions;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, orientedOptions);
  } catch {
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      return null;
    }
  }

  const { width, height } = fitDimensions(bitmap.width, bitmap.height, spec.maxEdge);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    bitmap.close();
    return null;
  }

  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const webp = await toBlob(canvas, "image/webp", spec.quality);
  if (webp) return { blob: webp, contentType: "image/webp" };

  // Safari below 14 cannot encode WebP. The object keeps its .webp path — the
  // served Content-Type is what browsers actually honour.
  const jpeg = await toBlob(canvas, "image/jpeg", spec.quality);
  return jpeg ? { blob: jpeg, contentType: "image/jpeg" } : null;
};
